import { toast } from 'sonner';

/** Un singur id: o a doua declanșare înlocuiește notificarea, nu adaugă una. */
export const SW_UPDATE_TOAST_ID = 'sw-update-available';

type Container = Pick<ServiceWorkerContainer, 'register' | 'controller'>;

/**
 * Înregistrează /sw.js și anunță (`onUpdateReady`) când s-a instalat o versiune nouă a lui;
 * verifică o versiune nouă la fiecare `checkEveryMs` cât pagina e deschisă. Întoarce oprirea —
 * cleanup-ul effect-ului React. Oprirea contează: la formatul numeric ro-RO toată aplicația se
 * remontează imediat după hidratare, iar fără ea rămâneau două ascultătoare pe aceeași
 * înregistrare → „Versiune nouă disponibilă” de două ori (28.09.2026).
 */
export function watchServiceWorkerUpdates(
  container: Container,
  onUpdateReady: () => void,
  checkEveryMs = 30 * 60 * 1000,
): () => void {
  let stopped = false;
  let registration: ServiceWorkerRegistration | null = null;
  let timer: ReturnType<typeof setInterval> | null = null;

  const onUpdateFound = () => {
    const newWorker = registration?.installing;
    if (!newWorker) return;
    newWorker.addEventListener('statechange', () => {
      // Fără `controller` e prima instalare pe pagina asta, nu o versiune nouă.
      if (!stopped && newWorker.state === 'installed' && container.controller) onUpdateReady();
    });
  };

  // Silent fail — SW e nice-to-have, nu break aplicația (nici când register() aruncă sincron)
  const fail = (err: unknown) => console.warn('[SW] Registration failed:', err);
  let registering: Promise<ServiceWorkerRegistration>;
  try {
    registering = container.register('/sw.js', { scope: '/' });
  } catch (err) {
    fail(err);
    return () => {};
  }
  registering
    .then((reg) => {
      if (stopped) return; // demontat înainte ca înregistrarea să răspundă
      registration = reg;
      reg.addEventListener('updatefound', onUpdateFound);
      timer = setInterval(() => reg.update().catch(() => {}), checkEveryMs);
    })
    .catch(fail);

  return () => {
    stopped = true;
    registration?.removeEventListener('updatefound', onUpdateFound);
    if (timer) clearInterval(timer);
  };
}

/** Anunțul unei versiuni noi; utilizatorul reîncarcă atunci când vrea (fără refresh forțat). */
export function showUpdateReadyToast() {
  toast('Versiune nouă disponibilă', {
    id: SW_UPDATE_TOAST_ID,
    description: 'Reîncarcă pagina pentru a o folosi.',
    duration: 30000,
    action: { label: 'Reîncarcă', onClick: () => window.location.reload() },
  });
}
